import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader from '../../components/common/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Download, FileText, Clock, DollarSign, Car, Pencil } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';

const TimesheetReportPage = () => {
  const { api } = useAuth();
  const [workers, setWorkers] = useState([]);
  const [selectedWorker, setSelectedWorker] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingShift, setEditingShift] = useState(null);
  const [editStart, setEditStart] = useState('');
  const [editEnd, setEditEnd] = useState('');

  useEffect(() => {
    const fetchWorkers = async () => {
      try {
        const res = await api.get('/users', { params: { role: 'worker' } });
        setWorkers(res.data || []);
      } catch (e) {}
    };
    fetchWorkers();

    // Default to last fortnight
    const now = new Date();
    const day = now.getDay();
    const diffToMon = day === 0 ? 6 : day - 1;
    const thisMonday = new Date(now);
    thisMonday.setDate(now.getDate() - diffToMon);
    const fortnightAgo = new Date(thisMonday);
    fortnightAgo.setDate(thisMonday.getDate() - 14);
    setStartDate(fortnightAgo.toISOString().split('T')[0]);
    setEndDate(thisMonday.toISOString().split('T')[0]);
  }, []);

  const handleGenerate = async () => {
    if (!selectedWorker) { toast.error('Please select a worker'); return; }
    setLoading(true);
    try {
      const res = await api.get('/reports/timesheet', {
        params: { worker_id: selectedWorker, start_date: startDate, end_date: endDate }
      });
      setReport(res.data);
    } catch (error) {
      toast.error('Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCSV = async () => {
    if (!selectedWorker) return;
    try {
      const res = await api.get('/reports/timesheet/csv', {
        params: { worker_id: selectedWorker, start_date: startDate, end_date: endDate },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const worker = workers.find(w => w.id === selectedWorker);
      link.download = `timesheet_${worker?.first_name || ''}_${worker?.last_name || ''}_${startDate}_to_${endDate}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download CSV');
    }
  };

  const handleEditShift = (row) => {
    setEditingShift(row);
    // Parse stored times back to input format (HH:MM)
    const parseTime = (timeStr) => {
      const match = timeStr.match(/(\d{1,2}):(\d{2})\s*(am|pm)/i);
      if (!match) return '';
      let h = parseInt(match[1]);
      const m = match[2];
      const ampm = match[3].toLowerCase();
      if (ampm === 'pm' && h !== 12) h += 12;
      if (ampm === 'am' && h === 12) h = 0;
      return String(h).padStart(2, '0') + ':' + m;
    };
    setEditStart(parseTime(row.scheduled_start));
    setEditEnd(parseTime(row.scheduled_end));
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingShift || !editStart || !editEnd) return;
    try {
      const dateStr = editingShift.date.split('/').reverse().join('-'); // DD/MM/YYYY -> YYYY-MM-DD
      await api.put('/reports/timesheet/shift/' + editingShift.shift_id, {
        start_time: dateStr + 'T' + editStart,
        end_time: dateStr + 'T' + editEnd
      });
      toast.success('Shift times updated');
      setEditDialogOpen(false);
      setEditingShift(null);
      handleGenerate();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="timesheet-report-page">
      <PageHeader
        title="Timesheet Reports"
        description="Generate fortnightly timesheets with hours breakdown, travel and expenses"
      />

      <Card className="border-border/40 bg-card/50 backdrop-blur-sm">
        <CardContent className="py-4">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 items-end">
            <div className="space-y-2">
              <Label>Worker</Label>
              <Select value={selectedWorker} onValueChange={setSelectedWorker}>
                <SelectTrigger data-testid="timesheet-worker"><SelectValue placeholder="Select worker" /></SelectTrigger>
                <SelectContent>
                  {workers.map(w => (
                    <SelectItem key={w.id} value={w.id}>{w.first_name} {w.last_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Start Date</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>End Date</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleGenerate} disabled={loading} data-testid="generate-btn">
                <FileText className="h-4 w-4 mr-2" />
                {loading ? 'Loading...' : 'Generate'}
              </Button>
              {report && (
                <Button variant="outline" onClick={handleDownloadCSV} data-testid="download-csv-btn">
                  <Download className="h-4 w-4 mr-2" />
                  CSV
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {report && (
        <>
          {/* Summary Cards */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Card className="border-border/40">
              <CardContent className="py-4 flex items-center gap-3">
                <Clock className="h-8 w-8 text-blue-500" />
                <div>
                  <p className="text-2xl font-bold">{report.totals.total_hours}h</p>
                  <p className="text-xs text-muted-foreground">Total Hours</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-border/40">
              <CardContent className="py-4 flex items-center gap-3">
                <Car className="h-8 w-8 text-green-500" />
                <div>
                  <p className="text-2xl font-bold">{report.totals.travel_km} km</p>
                  <p className="text-xs text-muted-foreground">Travel</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-border/40">
              <CardContent className="py-4 flex items-center gap-3">
                <DollarSign className="h-8 w-8 text-amber-500" />
                <div>
                  <p className="text-2xl font-bold">${report.totals.expenses.toFixed(2)}</p>
                  <p className="text-xs text-muted-foreground">Expenses</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-border/40">
              <CardContent className="py-4">
                <div className="grid grid-cols-2 gap-1 text-sm">
                  <span className="text-muted-foreground">Day:</span><span className="font-medium">{report.totals.day_hours}h</span>
                  <span className="text-muted-foreground">Arvo:</span><span className="font-medium">{report.totals.arvo_hours}h</span>
                  <span className="text-muted-foreground">Sat:</span><span className="font-medium">{report.totals.saturday_hours}h</span>
                  <span className="text-muted-foreground">Sun:</span><span className="font-medium">{report.totals.sunday_hours}h</span>
                  <span className="text-muted-foreground">Overnight:</span><span className="font-medium">{report.totals.overnight_count}</span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Shifts Table */}
          <Card className="border-border/40 bg-card/50 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="text-lg">{report.worker_name} — {report.period}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="py-2 pr-3 font-medium">Date</th>
                      <th className="py-2 pr-3 font-medium">Day</th>
                      <th className="py-2 pr-3 font-medium">House</th>
                      <th className="py-2 pr-3 font-medium">Time</th>
                      <th className="py-2 pr-2 font-medium text-right">Total</th>
                      <th className="py-2 pr-2 font-medium text-right">Day</th>
                      <th className="py-2 pr-2 font-medium text-right">Arvo</th>
                      <th className="py-2 pr-2 font-medium text-right">Sat</th>
                      <th className="py-2 pr-2 font-medium text-right">Sun</th>
                      <th className="py-2 pr-2 font-medium text-right">O/N</th>
                      <th className="py-2 pr-2 font-medium text-right">KM</th>
                      <th className="py-2 pr-2 font-medium text-right">$</th>
                      <th className="py-2 pr-1 font-medium text-right"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.shifts.map((row, i) => (
                      <tr key={i} className="border-b border-border/20 hover:bg-muted/30">
                        <td className="py-2 pr-3">{row.date}</td>
                        <td className="py-2 pr-3">{row.day_name.slice(0, 3)}</td>
                        <td className="py-2 pr-3">{row.house}</td>
                        <td className="py-2 pr-3 whitespace-nowrap">{row.scheduled_start}-{row.scheduled_end}</td>
                        <td className="py-2 pr-2 text-right font-medium">{row.total_hours}</td>
                        <td className="py-2 pr-2 text-right">{row.day_hours || ''}</td>
                        <td className="py-2 pr-2 text-right">{row.arvo_hours || ''}</td>
                        <td className="py-2 pr-2 text-right">{row.saturday_hours || ''}</td>
                        <td className="py-2 pr-2 text-right">{row.sunday_hours || ''}</td>
                        <td className="py-2 pr-2 text-right">{row.overnight_count || ''}</td>
                        <td className="py-2 pr-2 text-right">{row.travel_km || ''}</td>
                        <td className="py-2 pr-2 text-right">{row.expenses ? `$${row.expenses.toFixed(2)}` : ''}</td>
                        <td className="py-2 pr-1 text-right">
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => handleEditShift(row)}>
                            <Pencil className="h-3 w-3" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 font-bold">
                      <td className="py-2 pr-3" colSpan={4}>TOTALS</td>
                      <td className="py-2 pr-2 text-right">{report.totals.total_hours}</td>
                      <td className="py-2 pr-2 text-right">{report.totals.day_hours}</td>
                      <td className="py-2 pr-2 text-right">{report.totals.arvo_hours}</td>
                      <td className="py-2 pr-2 text-right">{report.totals.saturday_hours}</td>
                      <td className="py-2 pr-2 text-right">{report.totals.sunday_hours}</td>
                      <td className="py-2 pr-2 text-right">{report.totals.overnight_count}</td>
                      <td className="py-2 pr-2 text-right">{report.totals.travel_km}</td>
                      <td className="py-2 pr-2 text-right">${report.totals.expenses.toFixed(2)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
      {/* Edit Shift Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Shift Times</DialogTitle>
          </DialogHeader>
          {editingShift && (
            <div className="space-y-4 py-4">
              <p className="text-sm text-muted-foreground">{editingShift.date} - {editingShift.day_name} - {editingShift.house}</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Time</Label>
                  <Input type="time" value={editStart} onChange={(e) => setEditStart(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>End Time</Label>
                  <Input type="time" value={editEnd} onChange={(e) => setEditEnd(e.target.value)} />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveEdit}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TimesheetReportPage;
